import {
  Sparkles,
  RefreshCw,
  Tag,
  Info,
  Check,
  Lock,
  ShieldCheck
} from 'lucide-react';

const CheckoutOrderSummary = ({
  selectedPlan,
  isCustomDealCheckout,
  billingCycle,
  isMandateOnly,
  isTrialUser,
  isReactivating,
  formattedCycleEnd,
  activeDaysRemaining,
  showPerkInfoModal,
  setShowPerkInfoModal,
  totalPlanAmount,
  basePrice,
  appliedCoupon,
  discountAmount,
  subtotal,
  isSameState,
  cgstAmount,
  sgstAmount,
  totalGstAmount,
  amountDueToday,
  handleProceedToPayment,
  isProcessingPayment,
  formatCurrency
}) => {
  return (
    <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 rounded-2xl sm:rounded-3xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm space-y-4">
      <div>
        <span className="text-xs font-semibold uppercase tracking-wider text-[#067978]">
          {selectedPlan?.name} Plan · {isCustomDealCheckout ? 'Custom Deal' : billingCycle === 'year' ? 'Yearly' : 'Monthly'}
        </span>
        <h2 className="text-lg sm:text-xl font-semibold text-[#067978] mt-0.5">Order summary</h2>
      </div>

      {/* Special Upgrade Perk / Reactivation / Trial Mandate Setup Offer Card */}
      {isMandateOnly && (
        <div className="p-3 sm:p-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50 via-teal-50/60 to-emerald-50/40 border border-emerald-200/90 text-xs text-slate-800 shadow-xs space-y-2 relative">
          <div className="flex items-center justify-between gap-2 border-b border-emerald-200/60 pb-2">
            <div className="flex items-center gap-2 font-bold text-emerald-950 min-w-0">
              <div className="w-6 h-6 rounded-lg bg-[#067978] text-white flex items-center justify-center shrink-0 shadow-2xs">
                {isTrialUser ? <Sparkles size={13} /> : isReactivating ? <RefreshCw size={13} /> : <Tag size={13} />}
              </div>
              <span className="text-[11px] sm:text-xs uppercase tracking-tight font-extrabold text-[#067978] truncate">
                {isTrialUser ? 'Trial Period - Growth Mandate Setup' : isReactivating ? 'Reactivation Plan' : 'Special Upgrade Offer'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowPerkInfoModal(!showPerkInfoModal)}
              className="w-6 h-6 rounded-full bg-white border border-emerald-300 text-[#067978] hover:bg-emerald-100 transition-all flex items-center justify-center cursor-pointer shadow-2xs shrink-0 p-1"
              title="Click for billing details"
            >
              <Info size={13} className="text-[#067978] shrink-0" />
            </button>
          </div>

          <p className="text-[11px] sm:text-[11.5px] text-slate-700 leading-relaxed">
            {isTrialUser ? (
              <>Your free trial period - growth stays active until <span className="font-semibold text-[#067978]">{formattedCycleEnd}</span>. <b className="text-slate-900">₹0 plan charge today</b> — your first plan payment will occur automatically when your trial ends on <span className="font-semibold text-[#067978]">{formattedCycleEnd}</span>.</>
            ) : isReactivating ? (
              <>Your current cycle is paid until <span className="font-semibold text-[#067978]">{formattedCycleEnd}</span>. Next plan payment will be deducted automatically on <span className="font-semibold text-[#067978]">{formattedCycleEnd}</span>.</>
            ) : (
              <>Enjoy <span className="font-bold text-slate-900">{selectedPlan?.name} Tier</span> for the remaining <span className="font-bold text-emerald-800">{activeDaysRemaining} days</span> of your cycle at no extra cost!</>
            )}
          </p>

          {/* Info Popover Modal */}
          {showPerkInfoModal && (
            <div className="mt-2.5 p-3 sm:p-3.5 bg-white rounded-xl border border-emerald-300 shadow-xl text-[11px] text-slate-700 space-y-2 relative z-30 animate-modal-entry">
              <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
                <b className="text-emerald-900 font-semibold text-xs">
                  {isTrialUser ? 'How Trial Period - Growth Mandate Setup Works' : isReactivating ? 'How Reactivation Billing Works' : 'How Mid-Cycle Upgrade Works'}
                </b>
                <button
                  type="button"
                  onClick={() => setShowPerkInfoModal(false)}
                  className="text-slate-400 hover:text-slate-600 font-bold px-1 text-xs cursor-pointer"
                >
                  ✕
                </button>
              </div>
              <ul className="space-y-1.5 text-slate-600 leading-relaxed">
                {isTrialUser ? (
                  <>
                    <li>• Your free trial period - growth stays 100% active until <b className="text-slate-900">{formattedCycleEnd}</b>.</li>
                    <li>• Only a 100% refundable <b>₹5.00</b> bank verification fee is charged today to setup your automated e-mandate.</li>
                    <li>• Your first recurring plan payment of <b>{formatCurrency(totalPlanAmount)}</b> will occur automatically when your trial ends on <b>{formattedCycleEnd}</b>.</li>
                  </>
                ) : isReactivating ? (
                  <>
                    <li>• You already paid for your current cycle, which remains active until <b className="text-slate-900">{formattedCycleEnd}</b>.</li>
                    <li>• Only a 100% refundable <b>₹5.00</b> bank verification fee is charged today to setup your automated e-mandate.</li>
                    <li>• Your next recurring plan payment of <b>{formatCurrency(totalPlanAmount)}</b> will be deducted automatically on <b>{formattedCycleEnd}</b>.</li>
                  </>
                ) : (
                  <>
                    <li>• Your current cycle stays active until <b className="text-slate-900">{formattedCycleEnd}</b>.</li>
                    <li>• You get <b>instant access</b> to all {selectedPlan?.name} Tier features today completely <b>FREE</b>.</li>
                    <li>• Only a refundable <b>₹5.00</b> bank verification fee is charged today to setup your automated e-mandate.</li>
                    <li>• Your first recurring plan payment of <b>{formatCurrency(totalPlanAmount)}</b> will occur automatically on <b>{formattedCycleEnd}</b>.</li>
                  </>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Line Items */}
      <div className="space-y-2 text-xs text-slate-600 font-normal">
        <div className="flex justify-between items-center py-1 gap-2">
          <span className="truncate">{selectedPlan?.name} Tier Plan</span>
          <span className="font-medium text-slate-900 shrink-0">{formatCurrency(basePrice)}</span>
        </div>

        {appliedCoupon && (
          <div className="flex justify-between items-center text-emerald-700 font-semibold py-1">
            <span>Discount ({appliedCoupon.code})</span>
            <span>- {formatCurrency(discountAmount)}</span>
          </div>
        )}

        <div className="flex justify-between items-center text-slate-400 py-1">
          <span>Billed {billingCycle}ly · recurring</span>
          <span>/ {billingCycle}</span>
        </div>

        <div className="border-t border-slate-100 pt-2.5 flex justify-between items-center font-medium text-slate-800">
          <span>Subtotal</span>
          <span>{formatCurrency(subtotal)}</span>
        </div>

        {isSameState ? (
          <>
            <div className="flex justify-between items-center text-slate-500 py-0.5">
              <span>CGST @ 9%</span>
              <span>{formatCurrency(cgstAmount)}</span>
            </div>
            <div className="flex justify-between items-center text-slate-500 py-0.5">
              <span>SGST @ 9%</span>
              <span>{formatCurrency(sgstAmount)}</span>
            </div>
          </>
        ) : (
          <div className="flex justify-between items-center text-slate-500 py-0.5">
            <span>IGST @ 18%</span>
            <span>{formatCurrency(totalGstAmount)}</span>
          </div>
        )}

        {/* Total Line Item */}
        <div className="border-t border-slate-200 pt-3 mt-2 space-y-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm text-slate-900">Due today</span>
              {isMandateOnly && (
                <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold text-[#067978] bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full whitespace-nowrap shadow-2xs">
                  <Check size={11} className="stroke-[3] text-[#067978]" />
                  <span>100% Refundable</span>
                </span>
              )}
            </div>
            <span className="text-xl sm:text-2xl font-bold text-[#067978]">
              {formatCurrency(amountDueToday)}
            </span>
          </div>
          {isMandateOnly && (
            <p className="text-[11px] sm:text-xs text-slate-500 font-normal">
              then {formatCurrency(totalPlanAmount)} / {billingCycle === 'year' ? 'year' : 'month'} starting {formattedCycleEnd}
            </p>
          )}
        </div>
      </div>

      {/* Pay Button & Trust Indicators */}
      <div className="space-y-2.5 pt-1">
        <button
          type="button"
          onClick={() => handleProceedToPayment()}
          disabled={isProcessingPayment}
          className="w-full py-2.5 sm:py-3 px-4 bg-[#067978] hover:bg-[#05605f] text-white font-semibold text-xs sm:text-sm rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
        >
          {isProcessingPayment ? (
            <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Lock size={15} className="sm:w-4 sm:h-4" />
              <span>
                {isMandateOnly
                  ? `Authorize Mandate (${formatCurrency(amountDueToday)})`
                  : `Pay ${formatCurrency(amountDueToday)} securely`
                }
              </span>
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500 font-normal text-center">
          <ShieldCheck size={14} className="text-[#067978] shrink-0" />
          <span>Secured payment · UPI, cards, net-banking</span>
        </div>

        <div className="flex flex-wrap justify-center gap-1.5 text-[10px] font-medium text-slate-400 uppercase tracking-wider">
          <span className="border border-slate-200 rounded px-1.5 py-0.5 bg-white">UPI</span>
          <span className="border border-slate-200 rounded px-1.5 py-0.5 bg-white">VISA</span>
          <span className="border border-slate-200 rounded px-1.5 py-0.5 bg-white">MC</span>
          <span className="border border-slate-200 rounded px-1.5 py-0.5 bg-white">RuPay</span>
        </div>
      </div>

      {/* Recurring Consent Notice Box (Below Authorize Button) */}
      <div className="p-3 sm:p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 text-xs text-slate-600 leading-relaxed font-normal space-y-2">
        <b className="block font-semibold text-slate-900">You&apos;re setting up a recurring payment.</b>
        <span>
          By subscribing you authorise Trrip to auto-debit <b>{formatCurrency(totalPlanAmount)}</b> (incl. GST) every {billingCycle === 'year' ? 'year for 5 years' : 'month for 12 months'}.
        </span>
        <div className="pt-2 border-t border-slate-200/60 flex items-start sm:items-center gap-2 text-[11px] text-slate-500">
          <Info size={14} className="text-[#067978] shrink-0 mt-0.5 sm:mt-0" />
          <span>
            You&apos;ll get a reminder before every charge, and the next debit is on <b className="text-[#067978] font-semibold">{formattedCycleEnd}</b>.
          </span>
        </div>
      </div>

      <p className="text-center text-xs text-slate-500 font-normal pt-1">
        Need custom volume? <a href="/support" className="text-[#067978] font-semibold hover:underline">Talk to us about Enterprise →</a>
      </p>
    </aside>
  );
};

export default CheckoutOrderSummary;
